import { useDispatch } from "react-redux";
import { setAuthenticatedUser } from "store/actions/auth";
import { toast } from "react-toastify";
import { useCurrentUser, useLogout, useFetchRoles } from "utils/hooks";
import { useCallback } from "react";

interface UserData {
  isChunked?: boolean;
  chunks?: Record<string, any>[];
  totalChunks?: number;
  totalItems?: number;
  chunkSize?: number;
  [key: string]: any;
}

// Helper function to safely process chunked data
const processChunkedData = (data: UserData) => {
  try {
    if (!data || !data.chunks || !Array.isArray(data.chunks)) {
      console.error("Invalid chunked data format:", data);
      return null;
    }

    // Reconstruct the full data from chunks
    const fullData = {
      ...data,
      ...data.chunks.reduce(
        (acc: Record<string, any>, chunk: Record<string, any>) => ({
          ...acc,
          ...chunk,
        }),
        {}
      ),
    };

    // Clean up chunk-related fields
    delete fullData.chunks;
    delete fullData.isChunked;
    delete fullData.totalChunks;
    delete fullData.totalItems;
    delete fullData.chunkSize;

    return fullData;
  } catch (error) {
    console.error("Error processing chunked data:", error);
    return null;
  }
};

const useRefreshUser = () => {
  const dispatch = useDispatch();
  const { logOut } = useLogout();
  const { user } = useCurrentUser();
  const { fetchRoles } = useFetchRoles();

  const submitRefreshRequest = useCallback(() => {
    return new Promise((resolve) => {
      fetch(`${process.env.REACT_APP_API_URL}`, {
        method: "POST",
        credentials: "include",
        body: JSON.stringify({
          query: `
            query {
              refreshUser {
                userId
                firstName
                lastName
                email
                expiresIn
                type
                verified
                lastLogin
                isSubscribed
                createdAt
                digestSubscription
                defaultLanguage
                is2FAEnabled
                slug
                defaultReminder
                notificationTimes
                daysOfWeek
                notificationMessage
                color
                icon
                alias
                activeOrganizationId
                activeOrganization {
                  uuid
                  name
                  description
                  slug
                  createdAt
                  subscription {
                    uuid
                    status
                    subId
                    plan {
                      uuid
                      name
                      slug
                      description
                      price_per_seat
                      seat_limit
                      practice_limit
                      group_limit
                      is_default
                      stripe_product_id
                      stripe_price_id
                      price_per_seat_annual
                      stripe_annual_price_id
                      is_public
                      features
                      patientLimit
                      assignmentLimit
                      customAssignmentLimit
                      customResourceLimit
                      fileLimit
                      groupLimit
                    }
                  }
                }
                organizations {
                  organizationId
                  name
                }
                removedPracticeId
                pinCode
                pinVerified
                stripeCustomerId
                role
              }
            }
          `,
        }),
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          organizationId: user?.activeOrganizationId || "NA",
          permissionKey: "refresh_user",
        },
      })
        .then(async (res) => {
          if (res.status !== 200 && res.status !== 201) {
            const error = await res.json();
            if (error.errors[0].message.includes("BRKN_6001")) {
              toast.warn(`For security purposes please log in again.`);
              logOut();
            } else {
              toast.error(`Hmm, something went wrong.`);
              throw new Error("Failed!");
            }
          }
          return res.json();
        })
        .then((resData) => {
          if (resData.errors) {
            toast.error(
              `Hmm, something went wrong (Code: 09AIMN). ${resData.errors[0].message}`
            );
          } else {
            try {
              // Handle chunked responses
              const userData = resData.data.refreshUser as UserData;

              if (userData.isChunked) {
                console.log(
                  `Received chunked user data: ${userData.totalChunks} chunks, ${userData.totalItems} total items`
                );

                // Process chunked data
                const fullUserData = processChunkedData(userData);

                if (fullUserData) {
                  dispatch(setAuthenticatedUser(fullUserData));
                  resolve(userData);
                  Promise.resolve(fullUserData).then((r) => r);
                } else {
                  console.error("Failed to process chunked user data");
                  toast.error("Failed to refresh user data. Please try again.");
                }
              } else {
                dispatch(setAuthenticatedUser(userData));
                fetchRoles();
                resolve(userData);
                Promise.resolve(userData).then((r) => r);
              }
            } catch (error) {
              console.error("Error processing user data:", error);
              toast.error("Error processing user data. Please try again.");
            }
          }
        })
        .catch((err) => {
          console.error("Error refreshing user:", err);
          toast.error("Failed to refresh user data. Please try again.");
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, logOut]);

  return {
    submitRefreshRequest,
  };
};

export default useRefreshUser;
